import { useEffect } from "react"
import type { VoiceAudioLevelSample } from "@/types/voice-chat-rtc"

import { sampleVoiceAudioLevels } from "@/lib/storage/voice-chat-middleware"

export interface VoiceAudioLevels {
  // Smoothed 0..1 level of the user's mic (drives the main orb).
  mic: number
  // Smoothed 0..1 level of the assistant's playback (drives the halo and the
  // mini-player's bottom-edge glow).
  assistant: number
  // True once the assistant's playback has produced a real, meterable level.
  // Some browsers don't report an inbound audio level, so the caller falls
  // back to a synthetic pulse when this stays false.
  assistantMetered: boolean
}

export const SILENT_AUDIO_LEVELS: VoiceAudioLevels = {
  mic: 0,
  assistant: 0,
  assistantMetered: false,
}

// ~25Hz. Smooth enough for an orb, and cheap: getStats is polled off the main
// audio path, so this never touches the WebRTC audio graph.
export const SAMPLE_INTERVAL_MS = 40
// getStats audio levels sit low for normal speech (~0.05–0.3), so apply gain to
// spread talking across the 0..1 range — otherwise the orb barely moves.
const LEVEL_GAIN = 3.5
// The first assistant level above this proves the browser meters playback.
const ASSISTANT_METERED_FLOOR = 0.05
// Publish only on a change at least this big, not every sample.
const LEVEL_PUBLISH_DELTA = 0.015
// Asymmetric smoothing: rise fast so each syllable punches the level up, decay
// slow so it settles gently. A symmetric lerp smoothed away speech transients
// and left the orb breathing instead of pulsing.
const ATTACK = 0.6
const RELEASE = 0.15

function easeLevel(current: number, target: number): number {
  return current + (target - current) * (target > current ? ATTACK : RELEASE)
}

const toPeak = (level: number | null) =>
  level === null ? 0 : Math.min(level * LEVEL_GAIN, 1)

/**
 * A level that moves faster than React should render. Visualizations
 * subscribe and write it straight to the DOM (see VoiceOrb), so a 25Hz meter
 * costs a style write per sample rather than a render.
 */
export interface VoiceLevelStore<T> {
  get: () => T
  subscribe: (onChange: () => void) => () => void
}

export function createVoiceLevelStore<T>(initial: T) {
  let value = initial
  const listeners = new Set<() => void>()
  return {
    get: () => value,
    set(next: T) {
      if (Object.is(next, value)) return
      value = next
      for (const listener of listeners) listener()
    },
    subscribe(onChange: () => void) {
      listeners.add(onChange)
      return () => {
        listeners.delete(onChange)
      }
    },
  }
}

// What an inactive instance reads: silence that never changes.
const SILENT_LEVEL_STORE: VoiceLevelStore<VoiceAudioLevels> = {
  get: () => SILENT_AUDIO_LEVELS,
  subscribe: () => () => {},
}

/**
 * Turns raw getStats readings into the smoothed, gained levels an orb moves
 * to. One instance per poll, so voice mode's pooled sampler and the bounded
 * interview's private one (use-voice-interview-levels.ts) share every tuning
 * number above and differ only in where their readings come from.
 */
export class VoiceLevelSmoother {
  private mic = 0
  private assistant = 0
  private metered = false
  // The last levels handed out; a stable reference until they move enough to
  // publish again.
  published: VoiceAudioLevels = SILENT_AUDIO_LEVELS

  // Folds one reading in. Returns the new levels when they moved enough to be
  // worth drawing, null when the previous `published` still stands.
  step(reading: VoiceAudioLevelSample): VoiceAudioLevels | null {
    const assistantPeak = toPeak(reading.assistant)
    if (assistantPeak > ASSISTANT_METERED_FLOOR) this.metered = true

    this.mic = easeLevel(this.mic, toPeak(reading.mic))
    this.assistant = easeLevel(this.assistant, assistantPeak)

    const { published } = this
    if (
      Math.abs(this.mic - published.mic) <= LEVEL_PUBLISH_DELTA &&
      Math.abs(this.assistant - published.assistant) <= LEVEL_PUBLISH_DELTA &&
      this.metered === published.assistantMetered
    ) {
      return null
    }
    this.published = {
      mic: this.mic,
      assistant: this.assistant,
      assistantMetered: this.metered,
    }
    return this.published
  }

  // Back to rest, including the metered latch: the next session's browser may
  // not be the same one.
  reset() {
    this.mic = 0
    this.assistant = 0
    this.metered = false
    this.published = SILENT_AUDIO_LEVELS
  }
}

// ONE sampler for the whole app, fanned out to every hook instance.
//
// Several things read these levels at once during a live call — the overlay orb,
// the mini-player's orb, and the mini-player's speaking glow while a sheet covers
// the overlay. Per-instance intervals meant per-instance getStats polls at 25Hz
// each, plus per-instance smoothing state that could drift out of step. The
// sampler below runs while at least one subscriber is active and stops with the
// last one, so the cost is the same whether one surface is mounted or three.
const pooled = createVoiceLevelStore(SILENT_AUDIO_LEVELS)
let intervalId: number | null = null
// How many hook instances currently want metering. The poll runs while this is
// above zero.
let activeCount = 0
let inFlight = false
// Smoothing state is shared, so a component that mounts mid-session joins the
// stream already settled rather than ramping up from silence on its own clock.
const smoother = new VoiceLevelSmoother()

async function sample() {
  // Skip if a prior getStats hasn't resolved yet, so slow samples don't pile up
  // behind the interval.
  if (inFlight) return
  inFlight = true
  try {
    const reading = await sampleVoiceAudioLevels()
    // Stopped while this was in flight — the state has been reset, don't
    // resurrect it.
    if (intervalId === null) return

    const next = smoother.step(reading)
    if (next) pooled.set(next)
  } finally {
    inFlight = false
  }
}

function stopSampling() {
  if (intervalId === null) return
  window.clearInterval(intervalId)
  intervalId = null
  smoother.reset()
  // Whoever is still subscribed needs to see the reset to silence.
  pooled.set(SILENT_AUDIO_LEVELS)
}

/**
 * Samples WebRTC stats for render-only metering; the RTC engine separately owns
 * speech-gate authority for caption pacing. Do not reroute mic or playback
 * through Web Audio for metering: that can bypass Chrome's echo-processing path,
 * leak assistant audio into the mic, and re-trigger semantic VAD interruption.
 * `getStats` observes levels without changing media routing. A muted mic reports
 * ~0, and inactive sessions return all-zero levels.
 */
export function useVoiceAudioLevel(
  active: boolean
): VoiceLevelStore<VoiceAudioLevels> {
  useEffect(() => {
    // Inactive: nothing to meter, and the silent store below says so.
    if (!active || typeof window === "undefined") {
      return
    }
    activeCount += 1
    if (intervalId === null) {
      intervalId = window.setInterval(() => void sample(), SAMPLE_INTERVAL_MS)
    }
    return () => {
      activeCount -= 1
      // Last one out stops the poll.
      if (activeCount === 0) {
        stopSampling()
      }
    }
  }, [active])

  // An inactive instance reads silence, so someone else's metering never
  // reaches it.
  return active ? pooled : SILENT_LEVEL_STORE
}
